import { chatInputLimitError, ChatInputLimitError } from "@coldreach/utils/chat-input-budget";
import type { UIMessage } from "ai";

interface ChatFileInput {
    type: "file";
    mediaType: string;
    filename?: string;
    url: string;
    extractedText?: string;
}

interface BuildChatUserMessageParams {
    text: string;
    files: ChatFileInput[];
    id: string;
    uploadImage: (image: Blob, filename: string) => Promise<{ url: string }>;
}

export function chatUserText(text: string, files: ChatFileInput[]): string {
    const attachments = files.filter(file => file.extractedText != null)
        .map(file => `--- File: ${file.filename} ---\n${file.extractedText}\n--- End of ${file.filename} ---`);
    return [...attachments, ...(text ? [text] : [])].join("\n\n");
}

export async function buildChatUserMessage({
    text,
    files,
    id,
    uploadImage,
}: BuildChatUserMessageParams): Promise<UIMessage> {
    if (chatInputLimitError([{ type: "text", text: chatUserText(text, files) }])) throw new ChatInputLimitError();
    const nativeFiles: ChatFileInput[] = [];
    const imageUrls: string[] = [];

    for (const file of files) {
        if (file.extractedText != null) {
            continue;
        }

        let url = file.url;
        if (file.mediaType.startsWith("image/") && url.startsWith("data:")) {
            const filename = file.filename || "image";
            try {
                const response = await fetch(url);
                const blob = await response.blob();
                const uploaded = await uploadImage(blob, filename);
                url = uploaded.url;
                imageUrls.push(`[Uploaded Image: "${filename}" — CDN URL: ${url}]`);
            } catch (error) {
                const detail = error instanceof Error ? error.message : String(error);
                throw new Error(`Image upload failed for "${filename}": ${detail}`);
            }
        }

        nativeFiles.push({ ...file, url });
    }

    let finalText = text;
    if (imageUrls.length > 0) {
        const imageBlock = imageUrls.join("\n");
        finalText = finalText ? `${imageBlock}\n\n${finalText}` : imageBlock;
    }
    finalText = chatUserText(finalText, files);
    if (chatInputLimitError([{ type: "text", text: finalText }])) throw new ChatInputLimitError();

    const parts: UIMessage["parts"] = [];
    if (finalText) parts.push({ type: "text", text: finalText });
    for (const file of nativeFiles) {
        parts.push({ type: "file", mediaType: file.mediaType, url: file.url, filename: file.filename });
    }

    return { id, role: "user", parts };
}
